import { Box, Stack, Typography } from '@mui/material';
import { type StructuredContentDocument } from '@repo/supergraph/structuredContent/types';
import { generateSrcSet } from '@repo/ui/utils/getImageSrcSet';
import { SPACING_MAP } from '../../constants';
import type { Spacing } from '../../types';

type Props = StructuredContentDocument<{
  align?: 'center' | 'left' | 'right';
  alt?: string;
  caption?: string;
  id: string;
  naturalHeight: number;
  naturalWidth: number;
  spacing?: {
    bottom: Spacing;
    top: Spacing;
  };
  src: string;
  title?: string;
}>;

export function ContentImage({ props }: Props) {
  const imageSrcSet = generateSrcSet({
    source: props.src,
    width: props.naturalWidth,
    height: props.naturalHeight,
  });

  if (!imageSrcSet) return null;

  const image = (
    <Box
      alt={props.alt}
      component="img"
      id={props.id}
      srcSet={imageSrcSet}
      sx={{
        objectFit: 'contain',
        width: { xs: '100%', sm: props.naturalWidth },
        height: { xs: '100%', sm: props.naturalHeight },
      }}
      loading="lazy"
    />
  );

  return (
    <Box
      sx={{
        display: 'block',
        float: { sm: props.align === 'center' ? 'none' : props.align },
        marginX: 'auto',
        ...(props.align && {
          marginRight: { sm: props.align === 'left' ? 4 : 0 },
          marginLeft: { sm: props.align === 'right' ? 4 : 0 },
        }),
        ...(props.spacing && {
          bottom: SPACING_MAP[props.spacing.bottom] || 0,
          top: SPACING_MAP[props.spacing.top] || 0,
        }),
        maxWidth: props.naturalWidth,
      }}
      component="span"
    >
      {props.caption ? (
        <Stack component="span">
          {image}
          <Typography
            variant="bodySmall"
            sx={{
              color: 'neutral.light',
              lineHeight: '1.5rem',
            }}
          >
            {props.caption}
          </Typography>
        </Stack>
      ) : (
        image
      )}
    </Box>
  );
}
